import { placeClickToCall } from '../../lib/clickToCall';
import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { Plus, User, Mail, Phone, Edit2, Trash2, MessageCircle, PhoneCall, ChevronRight, X, Mic } from 'lucide-react';
import useArrowNav from '../../hooks/useArrowNav';
import VoiceIntakeModal from '../../components/VoiceIntakeModal';
import {
  safeAsync, Badge, emptyMsg, cardWrap, neuShadow, neuShadowSm,
  btnPrimary, neuShadowInset, CONTACT_TYPES
} from './crmShared';
import ContactSmartFilters, { applyChipFilters } from './ContactSmartFilters';
import CrmFilterBar from '../../components/CrmFilterBar/CrmFilterBar';
import FollowUpBadge from '../../components/FollowUpBadge';

/* ═══════════════════════════════════════════════════════════════════════════
   ContactsTab — Alphabetical accordion with unified CRM filter bar
   • Task #230: owns its own data — fetches via crm:filtered-list
   • Contacts grouped by last-name initial (A–Z, then #)
   • Each letter is a collapsible section
   ═══════════════════════════════════════════════════════════════════════════ */

export default function ContactsTab({
  allUsers, currentUser, canCreate,
  setShowAddContact, loadData, setEditContact, setShowEditContact,
  setShowSendModal, setSendChannel, setSendResult, setSendPrefill, loadExtended,
  onContactClick,
}) {
  // ── Own data state (Task #230) ────────────────────────────────────────
  const [contactList, setContactList] = useState([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);

  // Voice intake modal (Task #70, Manny P1) - mobile-first dictation-to-contact
  const [showVoiceIntake, setShowVoiceIntake] = useState(false);

  // Fetch from crm:filtered-list whenever filter changes.
  //
  // pageSize override (Task #251 — May 23 2026): CrmFilterBar defaults to
  // pageSize=100, which makes sense for paginated lists (Opportunities,
  // Tasks, Pipeline). The Contacts tab is NOT paginated — it groups all
  // contacts into A-Z letter buckets, so 100 truncates the list to W-Z+#
  // (the last 100 by sort order). Override pageSize to 25000 so the full
  // contact universe loads in one shot, matching how crm:contacts:list
  // has worked since Task #84 (LIMIT 10000 default).
  useEffect(() => {
    if (!activeFilter) return;
    let cancelled = false;
    setListLoading(true);
    window.zignagenixAPI.getCRMFilteredList({
      resource: 'contacts',
      ...activeFilter,
      pageSize: 25000,    // override the CrmFilterBar default of 100 — the
                          // contacts tab uses letter-bucket grouping, not pagination
    })
      .then(res => {
        if (!cancelled) {
          setContactList(Array.isArray(res?.rows) ? res.rows : []);
          setTotal(res?.total || 0);
        }
      })
      .catch(() => { if (!cancelled) setContactList([]); })
      .finally(() => { if (!cancelled) setListLoading(false); });
    return () => { cancelled = true; };
  }, [activeFilter]);

  // ── Expanded letter groups (Set of letters currently open) ────────────
  const [expandedLetters, setExpandedLetters] = useState(new Set());

  // ── Phase C: smart filter chips (multi-select) ────────────────────────
  const [smartChips, setSmartChips] = useState(() => {
    try {
      const raw = localStorage.getItem('crm.contacts.smartChips');
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem('crm.contacts.smartChips', JSON.stringify(smartChips)); } catch {}
  }, [smartChips]);

  // Task #58 (Jun 8 2026): force-remount key for CrmFilterBar. Bumping
  // this resets all of CrmFilterBar's internal state (owner, stage,
  // search, date, sort) back to the URL params — which the clearAll
  // handler below clears just before the bump. This avoids coupling
  // ContactsTab to CrmFilterBar's internals and avoids a full page
  // reload (which would feel jarring mid-demo).
  const [filterBarKey, setFilterBarKey] = useState(0);

  // Task #58: one-click reset for the dead-end empty state that happens
  // when persisted smart chips (or stacked filters) collapse the visible
  // contact list to zero. Clears smart chips, wipes the CrmFilterBar URL
  // params, and re-mounts CrmFilterBar so its initial-state reads see a
  // clean slate.
  const clearAllFilters = useCallback(() => {
    setSmartChips([]);
    try { localStorage.setItem('crm.contacts.smartChips', '[]'); } catch {}
    try {
      const url = new URL(window.location.href);
      // The CrmFilterBar URL keys (see CrmFilterBar.js pushUrlParams):
      ['fo', 'fs', 'fdf', 'fdr', 'fsb', 'fsd', 'fq'].forEach(k => url.searchParams.delete(k));
      window.history.replaceState(null, '', url.pathname + (url.search || '') + url.hash);
    } catch { /* URL unavailable — ignore */ }
    setFilterBarKey(k => k + 1);
  }, []);

  // ── Quick-jump letter ─────────────────────────────────────────────────
  const scrollContainerRef = useRef(null);

  const toggleLetter = useCallback((letter) => {
    setExpandedLetters(prev => {
      const next = new Set(prev);
      if (next.has(letter)) next.delete(letter);
      else next.add(letter);
      return next;
    });
  }, []);

  // ── Apply Phase C smart-chip filters before grouping ─────────────────
  const chipFiltered = useMemo(
    () => applyChipFilters(contactList, smartChips),
    [contactList, smartChips]
  );

  // ── Group contacts alphabetically by last name ────────────────────────
  // Exclude 'partner' type — those are shown in the dedicated Users tab.
  const grouped = useMemo(() => {
    const sorted = [...chipFiltered]
      .filter((c) => c.contact_type !== 'partner')
      .sort((a, b) => {
      const lastA = (a.last_name || '').toLowerCase();
      const lastB = (b.last_name || '').toLowerCase();
      if (lastA < lastB) return -1;
      if (lastA > lastB) return 1;
      const firstA = (a.first_name || '').toLowerCase();
      const firstB = (b.first_name || '').toLowerCase();
      return firstA < firstB ? -1 : firstA > firstB ? 1 : 0;
    });

    const groups = {};
    sorted.forEach(c => {
      const initial = (c.last_name || '').charAt(0).toUpperCase();
      const letter = /[A-Z]/.test(initial) ? initial : '#';
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(c);
    });

    // Sort keys: A-Z then #
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === '#') return 1;
      if (b === '#') return -1;
      return a.localeCompare(b);
    });

    const result = {};
    sortedKeys.forEach(k => { result[k] = groups[k]; });
    return result;
  }, [chipFiltered]);

  // Available letters for the quick-jump bar
  const availableLetters = Object.keys(grouped);

  // Note (Task #56, Jun 8 2026): expandAll previously had empty deps `[]`
  // while referencing `grouped` — a stale closure. On first render `grouped`
  // is `{}` (contacts haven't loaded yet) so the callback was forever bound
  // to `setExpandedLetters(new Set([]))` — clicking the button did nothing.
  // Fix: depend on `grouped` so the callback re-binds whenever data updates.
  // IMPORTANT: this must live AFTER the `grouped` useMemo above. The earlier
  // version placed it before, which crashed with a TDZ
  // "Cannot access 'grouped' before initialization" error at render time
  // (Task #57 hotfix).
  const expandAll = useCallback(() => {
    const allLetters = Object.keys(grouped);
    setExpandedLetters(new Set(allLetters));
  }, [grouped]);

  const collapseAll = useCallback(() => {
    setExpandedLetters(new Set());
  }, []);

  // Auto-expand all groups when any filter is active (non-empty contactList
  // arrived from server-side filtering means results are already narrowed).
  useEffect(() => {
    if (activeFilter && (activeFilter.search || activeFilter.owner || activeFilter.stages?.length)) {
      setExpandedLetters(new Set(Object.keys(grouped)));
    }
  }, [grouped, activeFilter]);

  // Task #56 (Jun 8 2026): on the very first data arrival (no filter applied
  // yet, no letters expanded), open the first letter group automatically so
  // the user sees contact names immediately instead of an empty alphabet
  // accordion. Only fires once — guarded by a ref so subsequent re-renders
  // (e.g. smart-chip filtering down to a different first letter) don't
  // override the user's expand/collapse choices.
  const initialExpandDoneRef = useRef(false);
  useEffect(() => {
    if (initialExpandDoneRef.current) return;
    const letters = Object.keys(grouped);
    if (letters.length === 0) return;
    initialExpandDoneRef.current = true;
    setExpandedLetters(new Set([letters[0]]));
  }, [grouped]);

  const scrollToLetter = useCallback((letter) => {
    const el = document.getElementById(`contact-group-${letter}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Also expand this letter
      setExpandedLetters(prev => {
        const next = new Set(prev);
        next.add(letter);
        return next;
      });
    }
  }, []);

  const totalCount = contactList.filter(c => c.contact_type !== 'partner').length;

  return (
    <>
      {/* ═══ Unified Filter Bar (Task #230) ═══ */}
      {/* key={filterBarKey} (Task #58) lets clearAllFilters force a clean
          re-mount so the URL-param init state we just wiped takes effect. */}
      <CrmFilterBar
        key={filterBarKey}
        resource="contacts"
        allUsers={allUsers}
        currentUser={currentUser}
        onFilterChange={setActiveFilter}
      />

      {/* ═══ Phase C: Smart Filter Chips + View Presets ═══ */}
      <ContactSmartFilters
        chips={smartChips}
        setChips={setSmartChips}
        totalCount={totalCount}
        filteredCount={chipFiltered.filter(c => c.contact_type !== 'partner').length}
      />

      {/* ═══ Stats + Controls ═══ */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 10, padding: '0 2px',
      }}>
        <div style={{ fontSize: 12, color: '#6C757D' }}>
          {listLoading
            ? <span>Loading…</span>
            : <><strong style={{ color: '#212529' }}>{total}</strong> contact{total !== 1 ? 's' : ''}</>}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={expandAll} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#2E8B98', fontWeight: 600 }}>Expand All</button>
          <span style={{ color: '#DEE2E6' }}>|</span>
          <button onClick={collapseAll} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#2E8B98', fontWeight: 600 }}>Collapse All</button>
          {canCreate && (
            <>
              {/* Task #70 (Manny P1): voice intake — mobile-first, fast intake
                  for sales reps on inbound calls. Positioned before Add Contact
                  so it's the first action on the touch-friendly mobile layout. */}
              <button
                onClick={() => setShowVoiceIntake(true)}
                style={{ ...btnPrimary, background: '#7C3AED' }}
                title="Dictate a new prospect's details — name, phone, email, condition. AI parses into structured fields."
              >
                <Mic size={14} /> Voice intake
              </button>
              <button onClick={() => setShowAddContact(true)} style={btnPrimary}>
                <Plus size={14} /> Add Contact
              </button>
            </>
          )}
        </div>
      </div>

      {/* Voice intake modal — Task #70 */}
      <VoiceIntakeModal
        open={showVoiceIntake}
        onClose={() => setShowVoiceIntake(false)}
        onSaved={() => { setShowVoiceIntake(false); if (typeof loadData === 'function') loadData(); }}
      />

      {/* ═══ Quick-Jump Alphabet Bar ═══ */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 12,
        padding: '6px 8px', background: '#F8F9FA', borderRadius: 10,
        boxShadow: neuShadowSm,
      }}>
        {'ABCDEFGHIJKLMNOPQRSTUVWXYZ#'.split('').map(letter => {
          const hasContacts = availableLetters.includes(letter);
          return (
            <button
              key={letter}
              onClick={() => hasContacts && scrollToLetter(letter)}
              style={{
                width: 26, height: 26, borderRadius: 6, border: 'none',
                background: hasContacts ? (expandedLetters.has(letter) ? '#2E8B98' : '#fff') : 'transparent',
                color: hasContacts ? (expandedLetters.has(letter) ? '#fff' : '#212529') : '#DEE2E6',
                fontSize: 11, fontWeight: 700, cursor: hasContacts ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: hasContacts ? neuShadowSm : 'none',
                transition: 'all .15s ease',
              }}
            >
              {letter}
            </button>
          );
        })}
      </div>

      {/* ═══ Alphabetical Groups ═══ */}
      {/* Task #58: detect BOTH zero-result conditions, not just one:
           (a) server-side filter returned 0 rows         (contactList.length === 0)
           (b) smart chips filtered everything out         (grouped is empty but contactList isn't)
          Either way we render the empty state with a one-click Clear that
          wipes smartChips + URL filter params + re-mounts CrmFilterBar.
          Previously case (b) showed nothing at all — silent footgun that
          stuck four chips like hot+highPriority+noNextAction+noDeposits
          and made the tab look broken. */}
      {!listLoading && Object.keys(grouped).length === 0 ? (
        <div style={{ ...emptyMsg, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 24 }}>
          <div style={{ color: '#6C757D', fontSize: 14 }}>
            {contactList.length === 0
              ? (activeFilter ? 'No contacts match your filters.' : 'No contacts found.')
              : `${contactList.length.toLocaleString()} contact${contactList.length !== 1 ? 's' : ''} loaded, but your active filters or smart chips matched none of them.`}
          </div>
          {(smartChips.length > 0 || (activeFilter && (activeFilter.search || activeFilter.owner || (activeFilter.stages && activeFilter.stages.length)))) && (
            <button
              onClick={clearAllFilters}
              style={{
                ...btnPrimary,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
              title="Reset every filter on this tab — smart chips, search, owner, stage, date, and sort"
            >
              <X size={14} /> Clear all filters
            </button>
          )}
        </div>
      ) : (
        <div ref={scrollContainerRef} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {Object.entries(grouped).map(([letter, groupContacts]) => (
            <LetterGroup
              key={letter}
              letter={letter}
              contacts={groupContacts}
              isExpanded={expandedLetters.has(letter)}
              onToggle={() => toggleLetter(letter)}
              setEditContact={setEditContact}
              setShowEditContact={setShowEditContact}
              setShowSendModal={setShowSendModal}
              setSendChannel={setSendChannel}
              setSendResult={setSendResult}
              setSendPrefill={setSendPrefill}
              loadData={loadData}
              onContactClick={onContactClick}
            />
          ))}
        </div>
      )}
    </>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   Single letter accordion group
   ═══════════════════════════════════════════════════════════════════════════ */

// React.memo: a LetterGroup only changes when its contacts array, expanded
// state, or any setter prop changes. With memo, expanding letter "S" doesn't
// re-render letters A-R or T-Z. Compounds with the ContactRow memo below.
const LetterGroup = React.memo(function LetterGroup({
  letter, contacts, isExpanded, onToggle,
  setEditContact, setShowEditContact,
  setShowSendModal, setSendChannel, setSendResult, setSendPrefill, loadData,
  onContactClick,
}) {
  const listRef = useRef(null);
  const { onKeyDown: handleListKeyDown } = useArrowNav(listRef, '[data-contact-row]', { horizontal: false, vertical: true });

  return (
    <div
      id={`contact-group-${letter}`}
      style={{
        background: '#F8F9FA', borderRadius: 12, boxShadow: neuShadowSm,
        overflow: 'hidden', transition: 'all .2s ease',
        border: isExpanded ? '2px solid #2E8B98' : '2px solid transparent',
      }}
    >
      {/* ─── Letter Header Button ─── */}
      <button
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
          padding: '10px 16px', border: 'none', cursor: 'pointer', textAlign: 'left',
          background: isExpanded ? '#2E8B9808' : '#F8F9FA',
          borderRadius: isExpanded ? '10px 10px 0 0' : 10,
          transition: 'background .15s ease',
        }}
        onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#f0f4f8'; }}
        onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = '#F8F9FA'; }}
      >
        <ChevronRight
          size={16}
          color={isExpanded ? '#2E8B98' : '#6C757D'}
          style={{
            transition: 'transform .25s ease',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            flexShrink: 0,
          }}
        />
        <span style={{
          fontSize: 18, fontWeight: 800, color: isExpanded ? '#2E8B98' : '#212529',
          width: 24, textAlign: 'center',
        }}>
          {letter}
        </span>
        <div style={{ flex: 1 }} />
        <Badge text={`${contacts.length} contact${contacts.length !== 1 ? 's' : ''}`} color={isExpanded ? '#2E8B98' : '#94a3b8'} />
      </button>

      {/* ─── Collapsible Contact List ─── */}
      {/* Lazy mount: only render the rows when this letter is expanded.
          Previously every letter's rows lived in the DOM at all times, hidden
          by CSS max-height:0. With 6k+ contacts that's 6k+ rows mounted on
          first render even though only one letter is open — the dominant cost
          on the Contacts tab. Conditional render here means we mount only the
          rows the user actually wants to see (typically a few hundred). */}
      {isExpanded && (
        <div style={{ padding: '0 4px 4px' }}>
          <div ref={listRef} onKeyDown={handleListKeyDown}>
            {contacts.map((c, i) => (
              <ContactRow
                key={c.id}
                contact={c}
                index={i}
                setEditContact={setEditContact}
                setShowEditContact={setShowEditContact}
                setShowSendModal={setShowSendModal}
                setSendChannel={setSendChannel}
                setSendResult={setSendResult}
                setSendPrefill={setSendPrefill}
                loadData={loadData}
                onContactClick={onContactClick}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
});


/* ═══════════════════════════════════════════════════════════════════════════
   Single contact row
   ═══════════════════════════════════════════════════════════════════════════ */

// React.memo: skip re-render when contact data + index haven't changed.
// Without memo, every parent re-render (e.g. expanding a different letter
// group) re-renders all visible rows, which is what made expansion feel
// 1-2 seconds slow. With memo, only the rows in the newly-expanded group
// render — unchanged rows skip work.
/*
 * ⚠ EXPORTED FOR TEST — Card P91e item 2.
 *
 * The row is where the Call button and its in-flight guard live, and a test
 * that only GREPPED THIS FILE for those strings is what let a ReferenceError
 * ship: `callingId` was declared in ContactsTab and used here, so every row
 * with a phone crashed the list. Driving the whole tab needs an activeFilter
 * and a filtered-list round trip, which tests the filter bar rather than the
 * button, so the row is reachable on its own.
 */
export const ContactRow = React.memo(function ContactRow({
  contact: c, index: i,
  setEditContact, setShowEditContact,
  setShowSendModal, setSendChannel, setSendResult, setSendPrefill, loadData,
  onContactClick,
}) {
  /*
   * ⛔⛔ IN-FLIGHT GUARD, ROW-LOCAL — Card P91e blocker 1.
   *
   * Twilio rings the REP's own phone, so a second click while the first is
   * being placed rings that rep twice: two legs, one human.
   *
   * ⚠⚠ P91d declared this state in ContactsTab and used it HERE. ContactRow is
   * a separate React.memo component, so `callingId` was never in scope: every
   * row with a phone threw a ReferenceError the moment a letter group opened,
   * and the Contacts list crashed.
   *
   * ⛔ The test meant to cover it only GREPPED THE SOURCE for those strings, so
   * it matched text that could never run. Row-local state is also the cheaper
   * fix — nothing new crosses the memo boundary.
   */
  const [calling, setCalling] = useState(false);
  const ct = CONTACT_TYPES[c.contact_type] || CONTACT_TYPES.other;

  return (
    <div
      data-contact-row
      tabIndex={0}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
        background: i % 2 === 0 ? '#fff' : '#F8F9FA', borderRadius: 8,
        marginBottom: 2, outline: 'none', transition: 'background .1s ease',
      }}
      onFocus={e => { e.currentTarget.style.backgroundColor = '#dce2e8'; }}
      onBlur={e => { e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#fff' : '#F8F9FA'; }}
    >
      {/* Avatar */}
      <div style={{
        width: 34, height: 34, borderRadius: 8,
        background: `${ct.color}18`, display: 'flex', alignItems: 'center',
        justifyContent: 'center', flexShrink: 0,
      }}>
        <User size={15} color={ct.color} />
      </div>

      {/* Name + details */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: onContactClick ? '#2E8B98' : '#212529', cursor: onContactClick ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: 6 }}
          onClick={onContactClick ? (e) => { e.stopPropagation(); onContactClick(c.id); } : undefined}>
          <span>{c.last_name}, {c.first_name}</span>
          {/* Lead Health Score chip — colored by band; AT RISK ring overlay if any flags are set */}
          {typeof c._leadScore === 'number' && (
            <span
              title={
                `Lead Score: ${c._leadScore}/100\n` +
                `Engagement: ${c._scoreCategories?.engagement?.points ?? '—'}/30\n` +
                `Recency: ${c._scoreCategories?.recency?.points ?? '—'}/25\n` +
                `Progress: ${c._scoreCategories?.progress?.points ?? '—'}/25\n` +
                `Value: ${c._scoreCategories?.value?.points ?? '—'}/20` +
                (c._isAtRisk
                  ? `\n\n⚠ AT RISK FLAGS:\n  ${(c._atRiskFlags || []).join('\n  ')}`
                  : '')
              }
              style={{
                background: c._leadBand === 'hot' ? '#15803D'
                  : c._leadBand === 'active' ? '#B45309'
                  : '#DC2626',
                color: 'white',
                fontSize: 10,
                fontWeight: 700,
                padding: '2px 6px',
                borderRadius: 999,
                lineHeight: '14px',
                outline: c._isAtRisk ? '2px solid #FBBF24' : 'none',
                outlineOffset: 1,
                cursor: 'help',
              }}
            >
              {c._leadScore}
              {c._isAtRisk && ' ⚠'}
            </span>
          )}
          {/* Stale-lead hazard chip (Task #240). Renders only when the server
              attached _staleHazard (i.e. INTEL_STALE_HAZARD_V1 is on AND a
              model artifact exists). Color by band: green/yellow/red. Hover
              reveals the 95% credible interval and the top drivers. */}
          {typeof c._staleHazard === 'number' && (
            <span
              data-testid="stale-hazard-chip"
              title={
                `Hazard of going cold (next 7d): ${(c._staleHazard * 100).toFixed(0)}%\n` +
                (Array.isArray(c._staleHazardCI)
                  ? `95% credible interval: ${(c._staleHazardCI[0] * 100).toFixed(0)}% – ${(c._staleHazardCI[1] * 100).toFixed(0)}%\n`
                  : '') +
                (Array.isArray(c._staleHazardDrivers) && c._staleHazardDrivers.length > 0
                  ? `\nTop drivers:\n  ${c._staleHazardDrivers
                      .map(d => `${d.contribution >= 0 ? '+' : ''}${d.contribution.toFixed(2)}  ${d.name}`)
                      .join('\n  ')}\n`
                  : '') +
                (c._staleHazardModelVersion ? `\nModel: ${c._staleHazardModelVersion}` : '')
              }
              style={{
                background: c._staleHazardBand === 'high' ? '#DC2626'
                  : c._staleHazardBand === 'mid' ? '#CA8A04'
                  : '#16A34A',
                color: 'white',
                fontSize: 10,
                fontWeight: 700,
                padding: '2px 6px',
                borderRadius: 999,
                lineHeight: '14px',
                cursor: 'help',
              }}
            >
              {Math.round(c._staleHazard * 100)}%
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: '#6C757D', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 2 }}>
          {/* Card P117 PR-A — the SAME badge the Daily Hit List shows, so a
              rep learns the four states once. Maria works both screens. */}
          <FollowUpBadge value={c.next_follow_up_date} compact />
          {c.organization && <span>{c.organization}</span>}
          {c.pipeline_stage && <span>&middot; {c.pipeline_stage}</span>}
        </div>
      </div>

      {/* Type badge */}
      <Badge text={ct.label} color={ct.color} />

      {/* Assigned to */}
      <div style={{ fontSize: 11, color: '#ADB5BD', width: 80, textAlign: 'right', flexShrink: 0 }}>
        {c.assigned_to_name?.split(' ')[0] || '—'}
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
        {c.phone && (
          <ActionBtn
            icon={<MessageCircle size={13} color="#62BB46" />}
            hoverBg="#dcfce7"
            title={`SMS to ${c.phone}`}
            onClick={() => { setSendPrefill({ contactId: c.id, toAddress: c.phone }); setSendChannel('sms'); setSendResult(null); setShowSendModal(true); }}
          />
        )}
        {c.email && (
          <ActionBtn
            icon={<Mail size={13} color="#2E8B98" />}
            hoverBg="#dbeafe"
            title={`Email ${c.email}`}
            onClick={() => { setSendPrefill({ contactId: c.id, toAddress: c.email }); setSendChannel('email'); setSendResult(null); setShowSendModal(true); }}
          />
        )}
        {c.phone && (
          <ActionBtn
            icon={<PhoneCall size={13} color="#F26522" />}
            hoverBg="#fef3c7"
            title={`Call ${c.phone}`}
            disabled={calling}
            onClick={safeAsync(async () => {
              if (calling) return;
              setCalling(true);
              try {
              /*
               * ⛔ ONE flow for all three Call buttons — src/lib/clickToCall.js.
               * This copy used to branch on the error PROSE (/agent phone/i);
               * P91 reworded the server message and the "save your number"
               * recovery silently stopped firing here while ContactDetailView
               * kept working. Three copies, two of them quietly broken.
               */
                const out = await placeClickToCall({
                  contactId: c.id, contactPhone: c.phone,
                  contactName: `${c.first_name || ''} ${c.last_name || ''}`,
                });
                if (out.cancelled) return;
                alert(out.message);
                if (out.ok) loadData();
              } finally {
                setCalling(false);
              }
            })}
          />
        )}
        <ActionBtn
          icon={<Edit2 size={13} color="#ADB5BD" />}
          hoverBg="#e0e5ec"
          title="Edit"
          onClick={() => { setEditContact(c); setShowEditContact(true); }}
        />
        <ActionBtn
          icon={<Trash2 size={13} color="#DC3545" />}
          hoverBg="#fee2e2"
          title="Delete"
          onClick={safeAsync(async () => { if (window.confirm(`Delete ${c.first_name} ${c.last_name}?`)) { await window.zignagenixAPI.deleteCRMContact(c.id); loadData(); } })}
        />
      </div>
    </div>
  );
});


/* ═══ Tiny action button helper ═══ */

function ActionBtn({ icon, hoverBg, title, onClick, disabled = false }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: '#F8F9FA', border: 'none', cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        padding: 4, borderRadius: 6, boxShadow: neuShadowSm,
        transition: 'all 0.15s ease', display: 'flex',
      }}
      title={title}
      onMouseEnter={e => { e.currentTarget.style.background = hoverBg; }}
      onMouseLeave={e => { e.currentTarget.style.background = '#F8F9FA'; }}
    >
      {icon}
    </button>
  );
}


